import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Autocomplete from '@mui/material/Autocomplete';
import SendIcon from '@mui/icons-material/Send';
import { Storyboard, Creator, SendToCreatorResult } from './types';
import { generateScriptPdfBlob, scriptPdfFilename, ScriptPdfReference } from './generateScriptPdf';
import CreatorsService from 'services/CreatorsService';
import VideoCreativeService from 'services/VideoCreativeService';

const creatorsService = new CreatorsService();
const ciService = new VideoCreativeService();

const DEFAULT_INCLUDE = { script: true, videoLink: true, keyframes: true, transcript: true, shotByShot: true };
type IncludeKey = keyof typeof DEFAULT_INCLUDE;

interface Props {
  open: boolean;
  onClose: () => void;
  // True while the parent is still loading the script detail + references.
  loading: boolean;
  storyboardId: string | null;
  storyboard: Storyboard | null;
  productName: string | null;
  references: ScriptPdfReference[];
  initialScript: string;
  // Called once a send succeeds, so the parent can refresh the script's
  // "Delivered (N)" status.
  onSent?: () => void;
}

// Send a finished script to a creator: pick the creator, set a script name, make
// final edits to the script / PDF, then send. The backend renders nothing — it
// receives the exact PDF built here and runs the Trello (+ later Drive/Notion)
// flow. Mirrors ScriptBriefDialog, plus the creator picker + script name.
export default function SendToCreatorDialog({ open, onClose, loading, storyboardId, storyboard, productName, references, initialScript, onSent }: Props) {
  const [creators, setCreators] = useState<Creator[]>([]);
  const [creatorsLoading, setCreatorsLoading] = useState(false);
  const [creator, setCreator] = useState<Creator | null>(null);
  const [scriptName, setScriptName] = useState('');
  const [scriptText, setScriptText] = useState('');
  const [include, setInclude] = useState(DEFAULT_INCLUDE);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Non-fatal warning when persisting the edited script back to the
  // storyboard fails — the send itself still proceeds.
  const [saveWarning, setSaveWarning] = useState<string | null>(null);
  const [result, setResult] = useState<SendToCreatorResult | null>(null);

  // Reset and load the creator list whenever the dialog (re-)opens.
  useEffect(() => {
    if (!open) return;
    setScriptText(initialScript);
    setScriptName(productName || '');
    setInclude(DEFAULT_INCLUDE);
    setCreator(null);
    setError(null);
    setSaveWarning(null);
    setResult(null);
    setCreatorsLoading(true);
    creatorsService.listCreators().then(res => {
      if (res) setCreators(res.creators);
      setCreatorsLoading(false);
    });
  }, [open, initialScript, productName]);

  const toggle = (key: IncludeKey) => setInclude(p => ({ ...p, [key]: !p[key] }));

  const canSend = !!storyboard && !!storyboardId && !!creator && scriptName.trim().length > 0 && !sending;

  const send = async () => {
    if (!storyboard || !storyboardId || !creator || !scriptName.trim()) return;
    setSending(true);
    setError(null);
    try {
      // If the user edited the script here, persist it back to the storyboard
      // first so the stored flat script matches what gets sent. Non-fatal —
      // warn and proceed with the edited text either way.
      // Saving re-flows the edited script into the segments' clip lines, so
      // re-read the storyboard before rendering — otherwise the PDF's per-clip
      // "suggested script to read" would still quote the pre-edit wording.
      let sb = storyboard;
      if (scriptText.trim() && scriptText.trim() !== (storyboard.flatScript || '').trim()) {
        const saved = await ciService.updateStoryboardFlatScript(storyboardId, scriptText).catch(() => null);
        if (!saved?.flatScriptEdited) {
          setSaveWarning("Couldn't save the edited script back to the storyboard — the send still used your edits.");
        } else if (saved.segmentsSynced) {
          const fresh = await ciService.getStoryboard(storyboardId).catch(() => null);
          if (fresh?.storyboard) {
            sb = fresh.storyboard;
          } else {
            // The save landed but the re-read didn't, so `sb` still holds the
            // pre-edit clip lines. Say so rather than shipping a PDF whose
            // per-clip "suggested script to read" silently contradicts the
            // script above it.
            setSaveWarning('Saved your edits, but the storyboard could not be re-read — the clip lines in this PDF may still show the previous wording.');
          }
        } else if (saved.syncReason) {
          setSaveWarning(`The clips still show the previous wording — ${saved.syncReason}.`);
        }
      }
      const pdfBlob = await generateScriptPdfBlob(sb, productName, references, { scriptText, include });
      const res = await ciService.sendStoryboardToCreator(storyboardId, {
        creatorId: creator.creatorId,
        scriptName: scriptName.trim(),
        scriptText,
        productName: productName || '',
        pdfBlob,
        pdfFilename: scriptPdfFilename(productName),
      });
      setResult(res);
      onSent?.();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to send to creator. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const hasRefs = references.length > 0;
  return (
    <Dialog open={open} onClose={() => !sending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Send to creator</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : !storyboard ? (
          <Alert severity="error">Couldn&apos;t load this script.</Alert>
        ) : result ? (
          <>
            {saveWarning && <Alert severity="warning" sx={{ mt: 1 }}>{saveWarning}</Alert>}
            <Alert severity="success" sx={{ mt: 1 }}>
              Sent to <strong>{result.creator.firstName} {result.creator.lastName}</strong> — card created in
              {' '}&ldquo;{result.trello.listName}&rdquo;.{' '}
              <Link href={result.trello.cardUrl} target="_blank" rel="noopener noreferrer">Open the Trello card</Link>.
            </Alert>
            {result.drive.ok ? (
              <Alert severity="success" sx={{ mt: 1 }}>
                Upload folder created in Drive ({result.drive.uploadFolderName}).{' '}
                <Link href={result.drive.uploadFolderLink} target="_blank" rel="noopener noreferrer">Open the folder</Link>.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                The Trello card was created, but the Drive folder step failed{result.drive.error ? `: ${result.drive.error}` : ''}.
              </Alert>
            )}
            {result.notion.ok ? (
              <Alert severity="success" sx={{ mt: 1 }}>
                Added to the UGC Tracker in Notion.{' '}
                <Link href={result.notion.pageUrl} target="_blank" rel="noopener noreferrer">Open the entry</Link>.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Couldn&apos;t add the Notion UGC Tracker row{result.notion.error ? `: ${result.notion.error}` : ''}.
              </Alert>
            )}
          </>
        ) : (
          <>
            <Autocomplete
              options={creators}
              loading={creatorsLoading}
              value={creator}
              onChange={(_e, v) => setCreator(v)}
              getOptionLabel={c => `${c.firstName} ${c.lastName}`}
              isOptionEqualToValue={(a, b) => a.creatorId === b.creatorId}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Creator"
                  placeholder="Choose a creator"
                  helperText="Manage creators in the Creators tab"
                />
              )}
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              label="Script name"
              value={scriptName}
              onChange={e => setScriptName(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              disabled={sending}
              helperText="Used as the Trello card title"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Make any final edits to the script and choose which sections to include in the PDF.
            </Typography>
            <FormGroup row sx={{ mb: 1 }}>
              <FormControlLabel control={<Checkbox size="small" checked={include.script} onChange={() => toggle('script')} />} label="Script" />
              <FormControlLabel control={<Checkbox size="small" checked={include.videoLink} onChange={() => toggle('videoLink')} disabled={!hasRefs} />} label="Video link" />
              <FormControlLabel control={<Checkbox size="small" checked={include.keyframes} onChange={() => toggle('keyframes')} disabled={!hasRefs} />} label="Keyframes" />
              <FormControlLabel control={<Checkbox size="small" checked={include.transcript} onChange={() => toggle('transcript')} disabled={!hasRefs} />} label="Original transcript" />
              <FormControlLabel control={<Checkbox size="small" checked={include.shotByShot} onChange={() => toggle('shotByShot')} />} label="Shot-by-shot" />
            </FormGroup>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>Script</Typography>
            <TextField
              value={scriptText}
              onChange={e => setScriptText(e.target.value)}
              fullWidth multiline minRows={10} maxRows={24}
              variant="outlined"
              disabled={sending || !include.script}
              placeholder="The script the creator will perform…"
            />
            {saveWarning && <Alert severity="warning" sx={{ mt: 1 }}>{saveWarning}</Alert>}
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>{result ? 'Close' : 'Cancel'}</Button>
        {!result && (
          <Button
            variant="contained"
            disabled={!canSend || loading}
            onClick={send}
            startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
          >
            {sending ? 'Sending…' : 'Send to creator'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
